import React, { useState } from "react";
import ErrorHandler from "./ErrorHandler";
import ChakraProviderWithNonce from "./ChakraProviderWithNonce";
import RailsTimeZone from "rails-timezone";

import {
  AdminApiClient,
  AdminApiContext,
  FormEditorV3,
  gregAdminTheme,
} from "@getregistered/greg-editors";

import railsFetch, { fetchJson } from "./railsFetch";
import { fieldsFromRails, fieldsToRails } from "./registrationFieldMapping";

export default function RegistrationFormEditor({
  pages,
  shoppingCartEnabled,
  shoppingCartCurrency = "$",
  sessionBookingEnabled = false,
  sessionSlotDescriptionsEnabled = false,
  sessionUpgradeOptionsEnabled = false,
  tracksEnabled = false,
  tracksBlockedByUntrackedRegistrations = false,
  roomingEnabled = false,
  guestBookingEnabled = false,
  maxGuestBookings = 10,
  guestTrackOptionalEmailEnabled = false,
  documentUploadsEnabled = false,
  documentOptionsEnabled = false,
  avatarFieldEnabled = false,
  metadataEnabled = false,
  leadScannersEnabled = false,
  occupancyPricingEnabled = false,
  protectAttendeeReferenceFields = true,
  registrationPhasesEnabled = false,
  customInputValidationEnabled = false,
  timezone,
  startDate,
  endDate,
  apiBaseUrl,
  saveUrl,
  previewsUrl,
}) {
  const [fields, setFields] = useState(fieldsFromRails(pages));
  const apiClient = new AdminApiClient(apiBaseUrl, railsFetch);

  const handleSave = async (newFields) => {
    const mappedFields = fieldsToRails(newFields);
    const response = await fetchJson(saveUrl, {
      method: "PUT",
      body: JSON.stringify({ form: { fields: mappedFields } }),
    });
    if (response.ok) {
      const savedFields = await response.json();
      setFields(fieldsFromRails(savedFields.pages));
      return true;
    } else if (response.status === 401) {
      window.alert("Session expired. Please login to continue");
      window.open(response.url);
    } else {
      window.alert(`Error saving form: status ${response.status}`);
    }
  };

  const handlePreview = async (newFields) => {
    const mappedFields = fieldsToRails(newFields);
    const response = await railsFetch(previewsUrl, {
      method: "POST",
      body: JSON.stringify({ form_preview: { fields: mappedFields } }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
    const currentPreviewUrl = response.headers.get("Location");
    window.open(currentPreviewUrl, "greg_form_preview");
  };

  return (
    <ErrorHandler>
      <ChakraProviderWithNonce theme={gregAdminTheme}>
        <AdminApiContext.Provider value={apiClient}>
          <FormEditorV3
            initialFields={fields}
            startDate={new Date(startDate)}
            endDate={new Date(endDate)}
            shoppingCartEnabled={shoppingCartEnabled}
            shoppingCartCurrency={shoppingCartCurrency}
            sessionBookingEnabled={sessionBookingEnabled}
            sessionSlotDescriptionsEnabled={sessionSlotDescriptionsEnabled}
            sessionUpgradeOptionsEnabled={sessionUpgradeOptionsEnabled}
            tracksEnabled={tracksEnabled}
            tracksBlockedByUntrackedRegistrations={
              tracksBlockedByUntrackedRegistrations
            }
            roomingEnabled={roomingEnabled}
            guestBookingEnabled={guestBookingEnabled}
            maxGuestBookings={maxGuestBookings}
            guestTrackOptionalEmailEnabled={guestTrackOptionalEmailEnabled}
            documentUploadsEnabled={documentUploadsEnabled}
            documentOptionsEnabled={documentOptionsEnabled}
            avatarFieldEnabled={avatarFieldEnabled}
            metadataEnabled={metadataEnabled}
            leadScannersEnabled={leadScannersEnabled}
            occupancyPricingEnabled={occupancyPricingEnabled}
            protectAttendeeReferenceFields={protectAttendeeReferenceFields}
            registrationPhasesEnabled={registrationPhasesEnabled}
            customInputValidationEnabled={customInputValidationEnabled}
            multipleSessionSlotChoices
            timezone={
              timezone !== undefined ? RailsTimeZone.from(timezone) : undefined
            }
            onSave={handleSave}
            onPreview={handlePreview}
          />
        </AdminApiContext.Provider>
      </ChakraProviderWithNonce>
    </ErrorHandler>
  );
}
